import { Check, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import _ from 'lodash';

import {
  TeamAccessViewModel,
  UserAccessViewModel,
} from '@/portainer/models/access';
import { isBE } from '@/react/portainer/feature-flags/feature-flags.service';
import { useCanManagePolicies } from '@/react/portainer/policies/utils/useCanManagePolicies';

import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { withMeta } from '@@/datatables/extend-options/withMeta';
import { LoadingButton } from '@@/buttons';
import { TextTip } from '@@/Tip/TextTip';
import { mergeOptions } from '@@/datatables/extend-options/mergeOptions';
import { LinkButton } from '@@/LinkButton';

import { useColumns } from './columns/useColumns';
import { Access } from './types';
import { RemoveAccessButton } from './RemoveAccessButton';

type Props = {
  tableKey: string;
  dataset?: Array<Access>;
  onRemove(items: Array<Access>): void;
  onUpdate?: (
    users: Array<UserAccessViewModel>,
    teams: Array<TeamAccessViewModel>
  ) => void;
  showWarning?: boolean;
  isUpdateEnabled?: boolean;
  showRoles?: boolean;
  inheritFrom?: boolean;
  policyId?: number;
  policyName?: string;
  isUpdatingAccess: boolean;
  isLoading: boolean;
};

export function AccessDatatable({
  dataset,
  tableKey,
  onRemove,
  onUpdate,
  showWarning = false,
  isUpdateEnabled = false,
  showRoles = false,
  inheritFrom = false,
  policyId = 0,
  policyName = '',
  isUpdatingAccess,
  isLoading,
}: Props) {
  const columns = useColumns({ showRoles, inheritFrom });
  const [store] = useState(() => createPersistedStore(tableKey));
  const tableState = useTableState(store, tableKey);
  const rolesState = useRolesState();
  const hasPolicyApplied = policyId > 0;
  const canManagePolicies = useCanManagePolicies();

  return (
    <Datatable
      data-cy="access-datatable"
      title="Access"
      titleIcon={UserX}
      dataset={dataset || []}
      isLoading={isLoading}
      columns={columns}
      settingsManager={tableState}
      getRowId={(row) => `${row.Type}-${row.Id}`}
      extendTableOptions={mergeOptions(
        withMeta({
          table: 'access-table',
          roles: rolesState,
        })
      )}
      isRowSelectable={({ original: item }) =>
        hasPolicyApplied ? false : !inheritFrom || !item.Inherited
      }
      renderTableActions={(selectedItems) => (
        <>
          {canManagePolicies && hasPolicyApplied ? (
            <LinkButton
              to="portainer.policy"
              params={{ id: policyId, tab: 'config' }}
              data-cy="view-policy-link"
              className="h-fit w-fit"
              size="small"
              color="light"
            >
              Manage RBAC policy &apos;{policyName}&apos;
            </LinkButton>
          ) : (
            <>
              <RemoveAccessButton
                items={selectedItems}
                onClick={onRemove}
                isLoading={isUpdatingAccess}
              />

              {isBE && isUpdateEnabled && (
                <LoadingButton
                  data-cy="update-access-button"
                  icon={Check}
                  disabled={rolesState.count === 0}
                  onClick={handleUpdate}
                  isLoading={isUpdatingAccess}
                  loadingText="Updating..."
                >
                  Update
                </LoadingButton>
              )}
            </>
          )}
        </>
      )}
      description={
        <div className="flex flex-col gap-4">
          {!hasPolicyApplied && (
            <>
              {inheritFrom && policyId === 0 && (
                <div className="small text-muted">
                  <div>
                    Access tagged as <code>inherited</code> are inherited from
                    the group access. They cannot be removed or modified at the
                    environment level but they can be overridden.
                  </div>
                  <div>
                    Access tagged as <code>override</code> are overriding the
                    group
                  </div>
                </div>
              )}
              {isBE && showWarning && isUpdateEnabled && (
                <TextTip>
                  <div className="text-warning-9 th-highcontrast:text-warning-1 th-dark:text-warning-7">
                    Updating user access will require the affected user(s) to
                    logout and login for the changes to be taken into account.
                  </div>
                </TextTip>
              )}
            </>
          )}
        </div>
      }
    />
  );

  function handleUpdate() {
    if (!onUpdate) {
      return;
    }
    const update = rolesState.getUpdate();
    const teamsAccess = getAccess(update.teams, 'team');
    const usersAccess = getAccess(update.users, 'user');

    onUpdate(usersAccess, teamsAccess);

    function getAccess(
      accesses: Record<number, number | undefined>,
      type: 'team' | 'user'
    ) {
      return _.compact(
        Object.entries(accesses).map(([strId, role]) => {
          if (!strId || !role) {
            return undefined;
          }

          const id = parseInt(strId, 10);
          const entity = dataset?.find(
            (item) => item.Type === type && item.Id === id
          );
          if (!entity) {
            return undefined;
          }

          return {
            ...entity,
            Role: {
              Id: role,
              Name: '',
            },
          };
        })
      );
    }
  }
}

function useRolesState() {
  const [teamRoles, setTeamRoles] = useState<
    Record<number, number | undefined>
  >({});
  const [userRoles, setUserRoles] = useState<
    Record<number, number | undefined>
  >({});

  const count = useMemo(
    () => Object.keys(teamRoles).length + Object.keys(userRoles).length,
    [teamRoles, userRoles]
  );

  return { getRoleValue, setRolesValue, getUpdate, count };

  function getRoleValue(id: number, entity: 'user' | 'team') {
    if (entity === 'team') {
      return teamRoles[id];
    }
    return userRoles[id];
  }

  function setRolesValue(
    id: number,
    entity: 'user' | 'team',
    value: number | undefined
  ) {
    if (entity === 'team') {
      setTeamRoles(updater);

      return;
    }

    setUserRoles(updater);

    function updater(roles: Record<number, number | undefined>) {
      const newRoles = { ...roles };
      if (typeof value === 'undefined') {
        delete newRoles[id];
      } else {
        newRoles[id] = value;
      }
      return newRoles;
    }
  }

  function getUpdate() {
    return {
      users: userRoles,
      teams: teamRoles,
    };
  }
}
